import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { apiError } from "@/lib/api";

const AuthCtx = createContext(null);

function toUser(session) {
  const u = session?.user;
  if (!u) return null;
  return {
    id: u.id,
    email: u.email,
    name: u.user_metadata?.name || u.email?.split("@")[0] || "Builder",
  };
}

function humaniseAuthError(error) {
  const msg = String(error?.message || error || "").toLowerCase();
  if (msg.includes("rate limit")) return new Error("Too many attempts. Please wait a minute and try again.");
  if (msg.includes("invalid login credentials")) return new Error("Wrong email or password.");
  if (msg.includes("email not confirmed")) return new Error("Please confirm your email address first.");
  if (msg.includes("user already registered")) return new Error("An account with this email already exists — try signing in instead.");
  if (msg.includes("email address") && msg.includes("invalid")) return new Error("Please enter a valid email address.");
  return error instanceof Error ? error : new Error(String(error));
}

export function AuthProvider({ children }) {
  // null = loading, false = anonymous, object = user
  const [user, setUser] = useState(null);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setUser(toUser(data.session) || false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      setUser(toUser(session) || false);
    });
    return () => { mounted = false; sub.subscription.unsubscribe(); };
  }, []);

  const login = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw humaniseAuthError(error);
    return toUser(data.session);
  }, []);

  const register = useCallback(async (name, email, password) => {
    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: { data: { name } },
    });
    if (error) throw humaniseAuthError(error);
    return { user: toUser(data.session), needs_confirmation: !data.session };
  }, []);

  const startDemo = useCallback(async () => {
    // Use Supabase Anonymous Sign-in — no email, no rate limit, instant provisioning.
    // Requires "Allow anonymous sign-ins" enabled in Supabase Auth settings.
    const { data, error } = await supabase.auth.signInAnonymously({
      options: { data: { name: "Demo Builder", is_demo: true } },
    });
    if (error) {
      const msg = String(error.message || "").toLowerCase();
      if (msg.includes("anonymous") && (msg.includes("disabled") || msg.includes("not enabled") || msg.includes("not allowed"))) {
        throw new Error("Demo mode isn't enabled yet. Please enable anonymous sign-ins in Supabase → Authentication → Providers → Anonymous.");
      }
      if (msg.includes("rate limit")) {
        throw new Error("Too many demo sessions started. Please wait a minute and try again.");
      }
      throw error;
    }
    if (!data.session) throw new Error("Demo session could not be created — please try again.");
    // Seed sample project data for this fresh anonymous user (backend uses the session token).
    try {
      const { api } = await import("@/lib/api");
      await api.post("/demo/seed");
    } catch (e) {
      // Seed failure is non-fatal — user still lands in an empty account they can populate.
      if (process.env.NODE_ENV !== "production") console.warn("Demo seed failed (non-fatal):", e);
    }
    return toUser(data.session);
  }, []);

  const requestPasswordReset = useCallback(async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
  }, []);

  const updatePassword = useCallback(async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, login, register, startDemo, requestPasswordReset, updatePassword, logout, apiError }),
    [user, login, register, startDemo, requestPasswordReset, updatePassword, logout]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
